import { useEffect, useRef } from 'react';
import { cn } from '@shared/lib/utils';

interface InvoiceActivityLoaderProps {
  className?: string;
}

const SPOKE_COUNT = 8;
/** Full opacity wave around the circle — slower than a typical spinner. */
const CYCLE_MS = 1400;

/**
 * Figma «Icon / Loader»: 64×64 circle, 8 static spokes.
 * Animation = sequential brightness (opacity), spokes do not rotate.
 */
export function InvoiceActivityLoader({ className }: InvoiceActivityLoaderProps) {
  const spokeRefs = useRef<(SVGRectElement | null)[]>([]);

  useEffect(() => {
    let frame = 0;
    const start = performance.now();

    const tick = (now: number) => {
      const phase = ((now - start) % CYCLE_MS) / CYCLE_MS; // 0..1
      const head = phase * SPOKE_COUNT;

      for (let i = 0; i < SPOKE_COUNT; i += 1) {
        const el = spokeRefs.current[i];
        if (!el) continue;

        // Distance from the bright “head” of the wave (0 = brightest)
        let dist = Math.abs(head - i);
        dist = Math.min(dist, SPOKE_COUNT - dist);

        // Smooth falloff: head ~1, far spokes ~0.18
        const t = Math.max(0, 1 - dist / 2.4);
        const opacity = 0.18 + 0.82 * t * t;
        el.setAttribute('fill-opacity', String(opacity));
      }

      frame = window.requestAnimationFrame(tick);
    };

    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, []);

  return (
    <div
      className={cn(
        'flex size-16 shrink-0 items-center justify-center rounded-full',
        'bg-[var(--base-sidebar-background,#FAFAFA)]',
        className
      )}
      role="status"
      aria-label="Loading"
    >
      <svg
        width="40"
        height="40"
        viewBox="0 0 40 40"
        fill="none"
        className="size-10 text-[var(--base-foreground,#0C0A09)]"
        aria-hidden
      >
        {Array.from({ length: SPOKE_COUNT }, (_, index) => {
          const angle = index * (360 / SPOKE_COUNT);
          return (
            <g key={index} transform={`rotate(${angle} 20 20)`}>
              {/* 2×9 bar, radius 2 — “border 2px / radius 2px” look */}
              <rect
                ref={(node) => {
                  spokeRefs.current[index] = node;
                }}
                x="19"
                y="4.5"
                width="2"
                height="9"
                rx="2"
                ry="2"
                fill="currentColor"
                fillOpacity={0.18}
              />
            </g>
          );
        })}
      </svg>
    </div>
  );
}
